import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Timer } from "lucide-react";
import { listScheduledRuns } from "@/lib/scheduled-runs.functions";
import {
  scheduledRunOutcomeLabel,
  type ScheduledRunExpectation,
  type ScheduledRunRow,
} from "@/lib/scheduled-runs";

export const Route = createFileRoute("/_admin/admin/scheduled-runs")({
  component: ScheduledRunsPage,
});

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function outcomeClasses(outcome: string): string {
  if (outcome === "erfolg" || outcome === "ohne_arbeit") return "text-brand-wahlen";
  if (outcome === "ausstehend") return "text-muted-foreground";
  return "text-destructive";
}

function ScheduledRunsPage() {
  const fetchRuns = useServerFn(listScheduledRuns);
  const { data, isLoading, error, refetch, isFetching } = useQuery<{
    runs: ScheduledRunRow[];
    expectations: ScheduledRunExpectation[];
  }>({
    queryKey: ["scheduled-runs"],
    queryFn: () => fetchRuns({ data: { limit: 150 } }),
  });

  const runs = data?.runs ?? [];
  const expectations = data?.expectations ?? [];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-primary">Wiederkehrende Läufe</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Jeder wiederkehrende Lauf hinterlässt hier Zeitpunkt und Ausgang, auch wenn er nichts
            zu tun hatte. Ein Lauf gilt erst als erfolgreich, wenn das Ziel geantwortet hat.
            Bleibt ein Lauf über seine Frist hinaus aus, erscheint er zusätzlich als Störung im
            Arbeitsvorrat.
          </p>
        </div>
        <Timer className="h-8 w-8 shrink-0 text-primary" aria-hidden />
      </div>

      <div className="mt-6">
        <button
          onClick={() => refetch()}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-secondary"
        >
          {isFetching ? "Lädt …" : "Aktualisieren"}
        </button>
      </div>

      {error ? (
        <p className="mt-6 text-sm text-destructive">
          Die Übersicht konnte nicht geladen werden. Bitte erneut versuchen.
        </p>
      ) : isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Lädt …</p>
      ) : (
        <>
          <h2 className="mt-8 font-display text-xl font-extrabold text-primary">
            Takt und Frist je Lauf
          </h2>
          <div className="mt-3 overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2.5">Lauf</th>
                  <th className="px-3 py-2.5">Takt</th>
                  <th className="px-3 py-2.5">Frist</th>
                  <th className="px-3 py-2.5">Letzter Lauf</th>
                  <th className="px-3 py-2.5">Zustand</th>
                </tr>
              </thead>
              <tbody>
                {expectations.map((e) => (
                  <tr key={e.job_name} className="border-t border-border align-top">
                    <td className="px-3 py-2.5">
                      <span className="font-mono text-xs">{e.job_name}</span>
                      {e.note ? (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{e.note}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{e.cadence}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{e.max_age}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      {formatDate(e.last_success_at)}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-semibold">
                      {e.overdue ? (
                        <span className="text-destructive">Überfällig</span>
                      ) : (
                        <span className="text-brand-wahlen">Im Takt</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="mt-10 font-display text-xl font-extrabold text-primary">Letzte Läufe</h2>
          {runs.length === 0 ? (
            <p className="mt-3 rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
              Noch keine Läufe erfasst.
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2.5">Zeitpunkt</th>
                    <th className="px-3 py-2.5">Lauf</th>
                    <th className="px-3 py-2.5">Ausgang</th>
                    <th className="px-3 py-2.5">Antwort</th>
                    <th className="px-3 py-2.5">Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((row) => (
                    <tr key={row.id} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground">
                        {formatDate(row.started_at)}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{row.job_name}</td>
                      <td className={"px-3 py-2.5 text-xs font-semibold " + outcomeClasses(row.outcome)}>
                        {scheduledRunOutcomeLabel(row.outcome)}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">
                        {row.status_code ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">
                        {row.summary ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
